import React, { useRef } from "react"

type GetDataFn = <F extends AnyFunction>(computeData: F) => ReturnType<F>

export interface PointerDragData<T extends HTMLElement | SVGElement> {
	currentTarget: T
	target: HTMLElement | SVGElement | null
	event: React.PointerEvent<T>
	x: number
	y: number
	deltaX: number
	deltaY: number
	startX: number
	startY: number
	isStart: boolean
	isEnd: boolean
	getData: GetDataFn
}

export interface PointerDragHandlers<T extends HTMLElement | SVGElement> {
	onDrag: (pointer: PointerDragData<T>) => void
	onPointerMove?: (e: React.PointerEvent<T>) => void
	onPointerDown?: (e: React.PointerEvent<T>) => void
	onPointerUp?: (e: React.PointerEvent<T>) => void
	onPointerOut?: (e: React.PointerEvent<T>) => void
}

export const usePointerDrag = <T extends HTMLElement | SVGElement>(
	handlers: PointerDragHandlers<T>,
) => {
	const pointRef = useRef<PointerDragData<T> | null>(null)
	const dataRef = useRef<any>(null)
	const targetRef = useRef<HTMLElement | SVGElement | null>(null)

	const getData: GetDataFn = (computeData) => {
		if (dataRef.current === null) {
			dataRef.current = computeData()
		}

		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return dataRef.current
	}

	const setPointerData = (e: React.PointerEvent<T>): PointerDragData<T> => {
		const x = e.pageX
		const y = e.pageY

		pointRef.current = pointRef.current ?? {
			x,
			y,
			startX: x,
			startY: y,
			deltaX: 0,
			deltaY: 0,
			isStart: true,
			isEnd: false,
			target: targetRef.current,
			currentTarget: e.currentTarget,
			event: e,
			getData,
		}

		pointRef.current.x = x
		pointRef.current.y = y
		pointRef.current.deltaX = x - pointRef.current.startX
		pointRef.current.deltaY = y - pointRef.current.startY
		pointRef.current.currentTarget = e.currentTarget
		pointRef.current.event = e

		return pointRef.current
	}

	const onPointerDown = (e: React.PointerEvent<T>) => {
		targetRef.current =
			e.target instanceof HTMLElement || e.target instanceof SVGElement ? e.target : null

		e.currentTarget.setPointerCapture(e.pointerId)
		e.preventDefault()

		handlers.onPointerDown?.(e)
	}

	const onPointerMove = (e: React.PointerEvent<T>) => {
		if (e.currentTarget.hasPointerCapture(e.pointerId)) {
			const pointer = setPointerData(e)

			if (pointer.isStart) {
				pointer.startX = pointer.x
				pointer.startY = pointer.y
			}

			handlers.onDrag(pointer)

			pointer.isStart = false
		} else {
			handlers.onPointerMove?.(e)
		}
	}

	const onPointerUp = (e: React.PointerEvent<T>) => {
		if (pointRef.current) {
			const pointer = setPointerData(e)

			pointer.isEnd = true

			handlers.onDrag(pointer)

			pointRef.current = null
			dataRef.current = null
		}

		handlers.onPointerUp?.(e)
	}

	const onPointerOut = (e: React.PointerEvent<T>) => {
		handlers.onPointerOut?.(e)
	}

	return {
		onPointerDown,
		onPointerMove,
		onPointerUp,
		onPointerOut,
	}
}
